import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { AdminSidebarLayout } from './SidebarLayout';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from './ui/table';
import { SearchBar, Pagination, FilterSelect, DateRangeFilter } from './ListControls';
import {
  Users, Wallet, TrendingUp, DollarSign, FileText, Download,
  Loader2, RefreshCw, BarChart3, ArrowUpRight, ArrowDownRight,
  CheckCircle, XCircle, Clock, FileDown
} from 'lucide-react';
import { toast } from 'sonner';
import { exportTableToPdf } from '../utils/pdfExport';

const fmt = (n) => new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP', minimumFractionDigits: 0 }).format(n || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-PH', { year: 'numeric', month: 'short', day: 'numeric' }) : '-';

export function AdminReportsPage() {
  const { api } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => { fetchSummary(); }, []);

  const fetchSummary = async () => {
    try {
      const { data } = await api.get('/api/reports/summary');
      setSummary(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  if (loading) {
    return (
      <AdminSidebarLayout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#00E5FF]" />
        </div>
      </AdminSidebarLayout>
    );
  }

  return (
    <AdminSidebarLayout>
      <div className="p-4 sm:p-6 lg:p-8" data-testid="admin-reports-page">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8">
          <div>
            <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-white">Reports</h1>
            <p className="text-[#A0B4CC] mt-1">Comprehensive platform reports with export</p>
          </div>
          <Button onClick={fetchSummary} variant="ghost" className="text-[#00E5FF]">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            {
              label: 'Total Members',
              value: summary?.total_members || 0,
              icon: Users,
              color: '#00E5FF',
              sub: (summary?.paid_members !== undefined)
                ? `${summary.paid_members || 0} paid · ${summary.free_members || 0} free`
                : null,
            },
            { label: 'Active Subs', value: summary?.active_subscriptions || 0, icon: CheckCircle, color: '#00E676' },
            { label: 'Total Wallet Value', value: fmt(summary?.total_wallet_value), icon: Wallet, color: '#007AFF' },
            { label: 'Total Withdrawn', value: fmt(summary?.total_withdrawn_amount), icon: DollarSign, color: '#FFEA00' },
          ].map((s) => (
            <Card key={s.label} className="bg-[#041A3B] border-white/10">
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${s.color}15` }}>
                    <s.icon className="w-4 h-4" style={{ color: s.color }} />
                  </div>
                </div>
                <p className="text-[#5C7599] text-xs mb-1">{s.label}</p>
                <p className="font-outfit text-xl font-semibold text-white">{s.value}</p>
                {s.sub && <p className="text-[#5C7599] text-[10px] mt-0.5">{s.sub}</p>}
              </CardContent>
            </Card>
          ))}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#041A3B] border border-white/10 mb-6 flex-wrap h-auto gap-1 p-1">
            {[
              { val: 'overview', label: 'Overview', icon: BarChart3 },
              { val: 'users', label: 'Users', icon: Users },
              { val: 'subscriptions', label: 'Subscriptions', icon: CheckCircle },
              { val: 'withdrawals', label: 'Withdrawals', icon: DollarSign },
              { val: 'referrals', label: 'Referrals', icon: TrendingUp },
              { val: 'wallets', label: 'Wallets', icon: Wallet },
            ].map(t => (
              <TabsTrigger key={t.val} value={t.val} className="data-[state=active]:bg-[#00E5FF] data-[state=active]:text-[#000B18] text-xs sm:text-sm">
                <t.icon className="w-3.5 h-3.5 mr-1.5" /> {t.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <DateRangeFilter dateFrom={dateFrom} dateTo={dateTo} onDateFromChange={setDateFrom} onDateToChange={setDateTo} />

          <TabsContent value="overview"><OverviewTab summary={summary} /></TabsContent>
          <TabsContent value="users"><UsersReportTab api={api} dateFrom={dateFrom} dateTo={dateTo} /></TabsContent>
          <TabsContent value="subscriptions"><SubscriptionsReportTab api={api} dateFrom={dateFrom} dateTo={dateTo} /></TabsContent>
          <TabsContent value="withdrawals"><WithdrawalsReportTab api={api} dateFrom={dateFrom} dateTo={dateTo} /></TabsContent>
          <TabsContent value="referrals"><ReferralsReportTab api={api} dateFrom={dateFrom} dateTo={dateTo} /></TabsContent>
          <TabsContent value="wallets"><WalletsReportTab api={api} dateFrom={dateFrom} dateTo={dateTo} /></TabsContent>
        </Tabs>
      </div>
    </AdminSidebarLayout>
  );
}

function OverviewTab({ summary }) {
  if (!summary) return null;
  const stats = [
    { label: 'Total Members', value: summary.total_members },
    { label: 'Active Subscriptions', value: summary.active_subscriptions },
    { label: 'Inactive Members', value: summary.inactive_members },
    { label: 'Total Initial Value', value: fmt(summary.total_initial_value) },
    { label: 'Total Wallet Value', value: fmt(summary.total_wallet_value) },
    { label: 'Total Referral Earnings', value: fmt(summary.total_referral_earnings) },
    { label: 'Total Withdrawals', value: summary.total_withdrawals },
    { label: 'Pending Withdrawals', value: summary.pending_withdrawals },
    { label: 'Completed Withdrawals', value: summary.completed_withdrawals },
    { label: 'Total Withdrawn', value: fmt(summary.total_withdrawn_amount) },
    { label: 'Total Bookings', value: summary.total_bookings },
  ];

  return (
    <Card className="bg-[#041A3B] border-white/10">
      <CardHeader>
        <CardTitle className="text-white font-outfit">Platform Overview</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {stats.map((s) => (
            <div key={s.label} className="p-4 rounded-lg bg-[#000B18]">
              <p className="text-[#5C7599] text-xs mb-1">{s.label}</p>
              <p className="font-outfit text-lg font-semibold text-white">{s.value}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function ReportTableWrapper({ children, title, onExport, exporting, onExportPdf, exportingPdf }) {
  return (
    <Card className="bg-[#041A3B] border-white/10">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-white font-outfit text-lg">{title}</CardTitle>
        <div className="flex items-center gap-2">
          <Button onClick={onExportPdf} disabled={exportingPdf} size="sm" className="btn-secondary" data-testid="export-pdf-btn">
            {exportingPdf ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <FileDown className="w-4 h-4 mr-1" />}
            PDF
          </Button>
          <Button onClick={onExport} disabled={exporting} size="sm" className="btn-secondary" data-testid="export-csv-btn">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />}
            CSV
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">{children}</CardContent>
    </Card>
  );
}

function UsersReportTab({ api, dateFrom, dateTo }) {
  const [data, setData] = useState({ users: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [role, setRole] = useState('');
  const [verification, setVerification] = useState('');
  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(20);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/api/reports/users?skip=${skip}&limit=${limit}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (status) url += `&status=${status}`;
      if (role) url += `&role=${role}`;
      if (verification) url += `&verification=${verification}`;
      if (dateFrom) url += `&date_from=${encodeURIComponent(dateFrom)}`;
      if (dateTo) url += `&date_to=${encodeURIComponent(dateTo)}`;
      const { data } = await api.get(url);
      setData(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api, skip, limit, search, status, role, verification, dateFrom, dateTo]);

  useEffect(() => { fetch(); }, [fetch]);
  useEffect(() => { setSkip(0); }, [search, status, role, verification, limit, dateFrom, dateTo]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      let url = `/api/reports/users/export?`;
      if (search) url += `search=${encodeURIComponent(search)}&`;
      if (status) url += `status=${status}&`;
      if (role) url += `role=${role}&`;
      if (verification) url += `verification=${verification}&`;
      if (dateFrom) url += `date_from=${encodeURIComponent(dateFrom)}&`;
      if (dateTo) url += `date_to=${encodeURIComponent(dateTo)}&`;
      const response = await api.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a'); a.href = blobUrl; a.download = 'users_report.csv'; a.click();
      toast.success('Users report exported');
    } catch (e) { toast.error('Export failed'); }
    setExporting(false);
  };

  const exportPdf = () => {
    setExportingPdf(true);
    try {
      const columns = ['Name', 'Email', 'Role', 'Status', 'Wallet', 'Referrals', 'Joined'];
      const rows = data.users.map(u => [
        u.name, u.email, u.role, u.subscription_status || 'inactive',
        fmt(u.wallet_value), String(u.referral_count), fmtDate(u.created_at)
      ]);
      exportTableToPdf('Users Report', columns, rows, 'users_report.pdf', {
        dateFrom, dateTo,
        filters: status ? `Status: ${status}` : '',
        stats: [{ label: 'Total Users', value: String(data.total) }]
      });
      toast.success('Users PDF exported');
    } catch (e) { toast.error('PDF export failed'); }
    setExportingPdf(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px]"><SearchBar value={search} onChange={setSearch} placeholder="Search users..." /></div>
        <FilterSelect value={status} onChange={setStatus} placeholder="All Statuses" options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }, { value: 'expired', label: 'Expired' }]} />
        <FilterSelect value={role} onChange={setRole} placeholder="All Roles" options={[{ value: 'member', label: 'Member' }, { value: 'merchant', label: 'Merchant' }, { value: 'mro', label: 'MRO' }, { value: 'cro', label: 'CRO' }, { value: 'moderator', label: 'Moderator' }, { value: 'admin', label: 'Admin' }]} />
        <FilterSelect value={verification} onChange={setVerification} placeholder="Verification" options={[{ value: 'verified', label: 'Verified' }, { value: 'pending', label: 'Pending' }, { value: 'rejected', label: 'Rejected' }, { value: 'unverified', label: 'Unverified' }]} />
      </div>
      <ReportTableWrapper title="Users Report" onExport={exportCsv} exporting={exporting} onExportPdf={exportPdf} exportingPdf={exportingPdf}>
        {loading ? <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#00E5FF] mx-auto" /></div> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10">
                  <TableHead className="text-[#A0B4CC]">Name</TableHead>
                  <TableHead className="text-[#A0B4CC]">Email</TableHead>
                  <TableHead className="text-[#A0B4CC]">Role</TableHead>
                  <TableHead className="text-[#A0B4CC]">Status</TableHead>
                  <TableHead className="text-[#A0B4CC]">Wallet</TableHead>
                  <TableHead className="text-[#A0B4CC]">Referrals</TableHead>
                  <TableHead className="text-[#A0B4CC]">Joined</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.users.map(u => (
                  <TableRow key={u.id} className="border-white/5 hover:bg-white/5">
                    <TableCell className="text-white font-medium">{u.name}</TableCell>
                    <TableCell className="text-[#A0B4CC] text-sm">{u.email}</TableCell>
                    <TableCell><Badge className={u.role === 'admin' ? 'bg-[#007AFF]/20 text-[#007AFF]' : 'bg-[#00E5FF]/20 text-[#00E5FF]'}>{u.role}</Badge></TableCell>
                    <TableCell><Badge className={u.subscription_status === 'active' ? 'bg-[#00E676]/20 text-[#00E676]' : 'bg-[#5C7599]/20 text-[#5C7599]'}>{u.subscription_status || 'inactive'}</Badge></TableCell>
                    <TableCell className="font-mono text-white text-sm">{fmt(u.wallet_value)}</TableCell>
                    <TableCell className="text-[#00E5FF] text-sm">{u.referral_count}</TableCell>
                    <TableCell className="text-[#5C7599] text-sm">{fmtDate(u.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </ReportTableWrapper>
      <Pagination skip={skip} limit={limit} total={data.total} onPageChange={setSkip} onLimitChange={(v) => { setLimit(v); setSkip(0); }} />
    </div>
  );
}

function SubscriptionsReportTab({ api, dateFrom, dateTo }) {
  const [data, setData] = useState({ subscriptions: [], total: 0, available_plans: [] });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [plan, setPlan] = useState('');
  const [lifetimeOnly, setLifetimeOnly] = useState(false);
  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(20);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/api/reports/subscriptions?skip=${skip}&limit=${limit}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (status) url += `&status=${status}`;
      if (plan) url += `&plan=${encodeURIComponent(plan)}`;
      if (lifetimeOnly) url += `&lifetime_only=true`;
      if (dateFrom) url += `&date_from=${encodeURIComponent(dateFrom)}`;
      if (dateTo) url += `&date_to=${encodeURIComponent(dateTo)}`;
      const { data } = await api.get(url);
      setData(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api, skip, limit, search, status, plan, lifetimeOnly, dateFrom, dateTo]);

  useEffect(() => { fetch(); }, [fetch]);
  useEffect(() => { setSkip(0); }, [search, status, plan, lifetimeOnly, limit, dateFrom, dateTo]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      let url = `/api/reports/subscriptions/export?`;
      if (search) url += `search=${encodeURIComponent(search)}&`;
      if (status) url += `status=${status}&`;
      if (plan) url += `plan=${encodeURIComponent(plan)}&`;
      if (lifetimeOnly) url += `lifetime_only=true&`;
      if (dateFrom) url += `date_from=${encodeURIComponent(dateFrom)}&`;
      if (dateTo) url += `date_to=${encodeURIComponent(dateTo)}&`;
      const response = await api.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a'); a.href = blobUrl; a.download = 'subscriptions_report.csv'; a.click();
      toast.success('Subscriptions report exported');
    } catch (e) { toast.error('Export failed'); }
    setExporting(false);
  };

  const exportPdf = () => {
    setExportingPdf(true);
    try {
      const columns = ['Name', 'Email', 'Status', 'Plan', 'Lifetime', 'Activated', 'Expires'];
      const rows = data.subscriptions.map(u => [
        u.name, u.email, u.subscription_status || '-', u.subscription_plan || '-',
        u.subscription_is_lifetime ? 'Yes' : 'No', fmtDate(u.subscription_activated_at), fmtDate(u.subscription_expires_at)
      ]);
      const filters = [status && `Status: ${status}`, plan && `Plan: ${plan}`].filter(Boolean).join(', ');
      exportTableToPdf('Subscriptions Report', columns, rows, 'subscriptions_report.pdf', {
        dateFrom, dateTo, filters,
        stats: [{ label: 'Total Subscriptions', value: String(data.total) }]
      });
      toast.success('Subscriptions PDF exported');
    } catch (e) { toast.error('PDF export failed'); }
    setExportingPdf(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px]"><SearchBar value={search} onChange={setSearch} placeholder="Search..." /></div>
        <FilterSelect value={status} onChange={setStatus} placeholder="All Statuses" options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }, { value: 'expired', label: 'Expired' }]} />
        <FilterSelect value={plan} onChange={setPlan} placeholder="All Plans" options={(data.available_plans || []).map(p => ({ value: p, label: p }))} />
        <label className="flex items-center gap-2 text-sm text-[#A0B4CC] cursor-pointer select-none" data-testid="lifetime-only-toggle">
          <input type="checkbox" checked={lifetimeOnly} onChange={(e) => setLifetimeOnly(e.target.checked)} className="accent-[#00E5FF]" />
          Lifetime only
        </label>
      </div>
      <ReportTableWrapper title="Subscriptions Report" onExport={exportCsv} exporting={exporting} onExportPdf={exportPdf} exportingPdf={exportingPdf}>
        {loading ? <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#00E5FF] mx-auto" /></div> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10">
                  <TableHead className="text-[#A0B4CC]">Name</TableHead>
                  <TableHead className="text-[#A0B4CC]">Email</TableHead>
                  <TableHead className="text-[#A0B4CC]">Status</TableHead>
                  <TableHead className="text-[#A0B4CC]">Plan</TableHead>
                  <TableHead className="text-[#A0B4CC]">Lifetime</TableHead>
                  <TableHead className="text-[#A0B4CC]">Activated</TableHead>
                  <TableHead className="text-[#A0B4CC]">Expires</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.subscriptions.map(u => (
                  <TableRow key={u.id} className="border-white/5 hover:bg-white/5">
                    <TableCell className="text-white font-medium">{u.name}</TableCell>
                    <TableCell className="text-[#A0B4CC] text-sm">{u.email}</TableCell>
                    <TableCell><Badge className={u.subscription_status === 'active' ? 'bg-[#00E676]/20 text-[#00E676]' : 'bg-[#5C7599]/20 text-[#5C7599]'}>{u.subscription_status || '-'}</Badge></TableCell>
                    <TableCell className="text-white">{u.subscription_plan || '-'}</TableCell>
                    <TableCell><Badge className={u.subscription_is_lifetime ? 'bg-[#007AFF]/20 text-[#007AFF]' : 'bg-[#5C7599]/20 text-[#5C7599]'}>{u.subscription_is_lifetime ? 'Yes' : 'No'}</Badge></TableCell>
                    <TableCell className="text-[#5C7599] text-sm">{fmtDate(u.subscription_activated_at)}</TableCell>
                    <TableCell className="text-[#5C7599] text-sm">{fmtDate(u.subscription_expires_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </ReportTableWrapper>
      <Pagination skip={skip} limit={limit} total={data.total} onPageChange={setSkip} onLimitChange={(v) => { setLimit(v); setSkip(0); }} />
    </div>
  );
}

function WithdrawalsReportTab({ api, dateFrom, dateTo }) {
  const [data, setData] = useState({ withdrawals: [], total: 0, total_amount: 0, total_fees: 0 });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [method, setMethod] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(20);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/api/reports/withdrawals?skip=${skip}&limit=${limit}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (status) url += `&status=${status}`;
      if (method) url += `&method=${method}`;
      if (minAmount) url += `&min_amount=${encodeURIComponent(minAmount)}`;
      if (maxAmount) url += `&max_amount=${encodeURIComponent(maxAmount)}`;
      if (dateFrom) url += `&date_from=${encodeURIComponent(dateFrom)}`;
      if (dateTo) url += `&date_to=${encodeURIComponent(dateTo)}`;
      const { data } = await api.get(url);
      setData(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api, skip, limit, search, status, method, minAmount, maxAmount, dateFrom, dateTo]);

  useEffect(() => { fetch(); }, [fetch]);
  useEffect(() => { setSkip(0); }, [search, status, method, minAmount, maxAmount, limit, dateFrom, dateTo]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      let url = `/api/reports/withdrawals/export?`;
      if (search) url += `search=${encodeURIComponent(search)}&`;
      if (status) url += `status=${status}&`;
      if (method) url += `method=${method}&`;
      if (minAmount) url += `min_amount=${encodeURIComponent(minAmount)}&`;
      if (maxAmount) url += `max_amount=${encodeURIComponent(maxAmount)}&`;
      if (dateFrom) url += `date_from=${encodeURIComponent(dateFrom)}&`;
      if (dateTo) url += `date_to=${encodeURIComponent(dateTo)}&`;
      const response = await api.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a'); a.href = blobUrl; a.download = 'withdrawals_report.csv'; a.click();
      toast.success('Withdrawals report exported');
    } catch (e) { toast.error('Export failed'); }
    setExporting(false);
  };

  const exportPdf = () => {
    setExportingPdf(true);
    try {
      const columns = ['User', 'Bank', 'Account', 'Amount', 'Fee', 'Status', 'Date'];
      const rows = data.withdrawals.map(w => [
        w.user_name || '-', w.bank_name || '-', w.bank_account_name || '-',
        fmt(w.amount), fmt(w.fee_amount), w.status, fmtDate(w.created_at)
      ]);
      exportTableToPdf('Withdrawals Report', columns, rows, 'withdrawals_report.pdf', {
        dateFrom, dateTo,
        filters: status ? `Status: ${status}` : '',
        stats: [
          { label: 'Total Amount', value: fmt(data.total_amount) },
          { label: 'Total Fees', value: fmt(data.total_fees) },
          { label: 'Count', value: String(data.total) }
        ]
      });
      toast.success('Withdrawals PDF exported');
    } catch (e) { toast.error('PDF export failed'); }
    setExportingPdf(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px]"><SearchBar value={search} onChange={setSearch} placeholder="Search withdrawals..." /></div>
        <FilterSelect value={status} onChange={setStatus} placeholder="All Statuses" options={[{ value: 'pending', label: 'Pending' }, { value: 'processing', label: 'Processing' }, { value: 'completed', label: 'Completed' }, { value: 'rejected', label: 'Rejected' }]} />
        <FilterSelect value={method} onChange={setMethod} placeholder="All Methods" options={[{ value: 'gcash', label: 'GCash' }, { value: 'paymaya', label: 'PayMaya' }, { value: 'bank', label: 'Bank' }]} />
        <input type="number" min="0" step="any" value={minAmount} onChange={(e) => setMinAmount(e.target.value)} placeholder="Min ₱" className="w-28 h-9 px-3 rounded-md bg-[#000B18] border border-white/15 text-white text-sm" data-testid="wd-min-amount" />
        <input type="number" min="0" step="any" value={maxAmount} onChange={(e) => setMaxAmount(e.target.value)} placeholder="Max ₱" className="w-28 h-9 px-3 rounded-md bg-[#000B18] border border-white/15 text-white text-sm" data-testid="wd-max-amount" />
      </div>
      {data.total_amount > 0 && (
        <div className="flex gap-4">
          <div className="px-4 py-2 rounded-lg bg-[#041A3B] border border-white/10"><span className="text-[#5C7599] text-xs">Total Amount:</span> <span className="text-white font-mono ml-2">{fmt(data.total_amount)}</span></div>
          <div className="px-4 py-2 rounded-lg bg-[#041A3B] border border-white/10"><span className="text-[#5C7599] text-xs">Total Fees:</span> <span className="text-[#FFEA00] font-mono ml-2">{fmt(data.total_fees)}</span></div>
        </div>
      )}
      <ReportTableWrapper title="Withdrawals Report" onExport={exportCsv} exporting={exporting} onExportPdf={exportPdf} exportingPdf={exportingPdf}>
        {loading ? <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#00E5FF] mx-auto" /></div> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10">
                  <TableHead className="text-[#A0B4CC]">User</TableHead>
                  <TableHead className="text-[#A0B4CC]">Bank</TableHead>
                  <TableHead className="text-[#A0B4CC]">Account</TableHead>
                  <TableHead className="text-[#A0B4CC]">Amount</TableHead>
                  <TableHead className="text-[#A0B4CC]">Fee</TableHead>
                  <TableHead className="text-[#A0B4CC]">Status</TableHead>
                  <TableHead className="text-[#A0B4CC]">Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.withdrawals.map(w => (
                  <TableRow key={w.id} className="border-white/5 hover:bg-white/5">
                    <TableCell className="text-white font-medium">{w.user_name || '-'}</TableCell>
                    <TableCell className="text-[#A0B4CC] text-sm">{w.bank_name || '-'}</TableCell>
                    <TableCell className="text-[#A0B4CC] text-sm">{w.bank_account_name || '-'}</TableCell>
                    <TableCell className="font-mono text-white">{fmt(w.amount)}</TableCell>
                    <TableCell className="font-mono text-[#FFEA00] text-sm">{fmt(w.fee_amount)}</TableCell>
                    <TableCell><Badge className={w.status === 'completed' ? 'bg-[#00E676]/20 text-[#00E676]' : w.status === 'pending' ? 'bg-[#FFEA00]/20 text-[#FFEA00]' : w.status === 'rejected' ? 'bg-[#FF1744]/20 text-[#FF1744]' : 'bg-[#007AFF]/20 text-[#007AFF]'}>{w.status}</Badge></TableCell>
                    <TableCell className="text-[#5C7599] text-sm">{fmtDate(w.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </ReportTableWrapper>
      <Pagination skip={skip} limit={limit} total={data.total} onPageChange={setSkip} onLimitChange={(v) => { setLimit(v); setSkip(0); }} />
    </div>
  );
}

function ReferralsReportTab({ api, dateFrom, dateTo }) {
  const [data, setData] = useState({ referrals: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [search, setSearch] = useState('');
  const [hasReferrals, setHasReferrals] = useState(false);
  const [minEarnings, setMinEarnings] = useState('');
  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(20);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/api/reports/referrals?skip=${skip}&limit=${limit}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (hasReferrals) url += `&has_referrals=true`;
      if (minEarnings) url += `&min_earnings=${encodeURIComponent(minEarnings)}`;
      if (dateFrom) url += `&date_from=${encodeURIComponent(dateFrom)}`;
      if (dateTo) url += `&date_to=${encodeURIComponent(dateTo)}`;
      const { data } = await api.get(url);
      setData(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api, skip, limit, search, hasReferrals, minEarnings, dateFrom, dateTo]);

  useEffect(() => { fetch(); }, [fetch]);
  useEffect(() => { setSkip(0); }, [search, hasReferrals, minEarnings, limit, dateFrom, dateTo]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      let url = `/api/reports/referrals/export?`;
      if (search) url += `search=${encodeURIComponent(search)}&`;
      if (hasReferrals) url += `has_referrals=true&`;
      if (minEarnings) url += `min_earnings=${encodeURIComponent(minEarnings)}&`;
      if (dateFrom) url += `date_from=${encodeURIComponent(dateFrom)}&`;
      if (dateTo) url += `date_to=${encodeURIComponent(dateTo)}&`;
      const response = await api.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a'); a.href = blobUrl; a.download = 'referrals_report.csv'; a.click();
      toast.success('Referrals report exported');
    } catch (e) { toast.error('Export failed'); }
    setExporting(false);
  };

  const exportPdf = () => {
    setExportingPdf(true);
    try {
      const columns = ['Name', 'Email', 'Code', 'Level 1', 'Level 2', 'Total', 'Earnings', 'Joined'];
      const rows = data.referrals.map(u => [
        u.name, u.email, u.referral_code, String(u.level_1), String(u.level_2),
        String(u.total_referrals), fmt(u.referral_earnings), fmtDate(u.created_at)
      ]);
      exportTableToPdf('Referrals Report', columns, rows, 'referrals_report.pdf', {
        dateFrom, dateTo,
        stats: [{ label: 'Total Referrers', value: String(data.total) }]
      });
      toast.success('Referrals PDF exported');
    } catch (e) { toast.error('PDF export failed'); }
    setExportingPdf(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px]"><SearchBar value={search} onChange={setSearch} placeholder="Search referrals..." /></div>
        <label className="flex items-center gap-2 text-sm text-[#A0B4CC] cursor-pointer select-none" data-testid="has-referrals-toggle">
          <input type="checkbox" checked={hasReferrals} onChange={(e) => setHasReferrals(e.target.checked)} className="accent-[#00E5FF]" />
          Has referrals only
        </label>
        <input type="number" min="0" step="any" value={minEarnings} onChange={(e) => setMinEarnings(e.target.value)} placeholder="Min earnings ₱" className="w-32 h-9 px-3 rounded-md bg-[#000B18] border border-white/15 text-white text-sm" data-testid="min-earnings-input" />
      </div>
      <ReportTableWrapper title="Referrals Report" onExport={exportCsv} exporting={exporting} onExportPdf={exportPdf} exportingPdf={exportingPdf}>
        {loading ? <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#00E5FF] mx-auto" /></div> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10">
                  <TableHead className="text-[#A0B4CC]">Name</TableHead>
                  <TableHead className="text-[#A0B4CC]">Email</TableHead>
                  <TableHead className="text-[#A0B4CC]">Code</TableHead>
                  <TableHead className="text-[#A0B4CC]">Level 1</TableHead>
                  <TableHead className="text-[#A0B4CC]">Level 2</TableHead>
                  <TableHead className="text-[#A0B4CC]">Total</TableHead>
                  <TableHead className="text-[#A0B4CC]">Earnings</TableHead>
                  <TableHead className="text-[#A0B4CC]">Joined</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.referrals.map(u => (
                  <TableRow key={u.id} className="border-white/5 hover:bg-white/5">
                    <TableCell className="text-white font-medium">{u.name}</TableCell>
                    <TableCell className="text-[#A0B4CC] text-sm">{u.email}</TableCell>
                    <TableCell className="font-mono text-[#00E5FF] text-sm">{u.referral_code}</TableCell>
                    <TableCell className="text-white">{u.level_1}</TableCell>
                    <TableCell className="text-white">{u.level_2}</TableCell>
                    <TableCell className="font-semibold text-[#00E5FF]">{u.total_referrals}</TableCell>
                    <TableCell className="font-mono text-[#00E676]">{fmt(u.referral_earnings)}</TableCell>
                    <TableCell className="text-[#5C7599] text-sm">{fmtDate(u.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </ReportTableWrapper>
      <Pagination skip={skip} limit={limit} total={data.total} onPageChange={setSkip} onLimitChange={(v) => { setLimit(v); setSkip(0); }} />
    </div>
  );
}

function WalletsReportTab({ api, dateFrom, dateTo }) {
  const [data, setData] = useState({ wallets: [], total: 0, available_plans: [] });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [search, setSearch] = useState('');
  const [plan, setPlan] = useState('');
  const [hasBalance, setHasBalance] = useState(false);
  const [minValue, setMinValue] = useState('');
  const [maxValue, setMaxValue] = useState('');
  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(20);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/api/reports/wallets?skip=${skip}&limit=${limit}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (plan) url += `&plan=${encodeURIComponent(plan)}`;
      if (hasBalance) url += `&has_balance=true`;
      if (minValue) url += `&min_value=${encodeURIComponent(minValue)}`;
      if (maxValue) url += `&max_value=${encodeURIComponent(maxValue)}`;
      if (dateFrom) url += `&date_from=${encodeURIComponent(dateFrom)}`;
      if (dateTo) url += `&date_to=${encodeURIComponent(dateTo)}`;
      const { data } = await api.get(url);
      setData(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api, skip, limit, search, plan, hasBalance, minValue, maxValue, dateFrom, dateTo]);

  useEffect(() => { fetch(); }, [fetch]);
  useEffect(() => { setSkip(0); }, [search, plan, hasBalance, minValue, maxValue, limit, dateFrom, dateTo]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      let url = `/api/reports/wallets/export?`;
      if (search) url += `search=${encodeURIComponent(search)}&`;
      if (plan) url += `plan=${encodeURIComponent(plan)}&`;
      if (hasBalance) url += `has_balance=true&`;
      if (minValue) url += `min_value=${encodeURIComponent(minValue)}&`;
      if (maxValue) url += `max_value=${encodeURIComponent(maxValue)}&`;
      if (dateFrom) url += `date_from=${encodeURIComponent(dateFrom)}&`;
      if (dateTo) url += `date_to=${encodeURIComponent(dateTo)}&`;
      const response = await api.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a'); a.href = blobUrl; a.download = 'wallets_report.csv'; a.click();
      toast.success('Wallets report exported');
    } catch (e) { toast.error('Export failed'); }
    setExporting(false);
  };

  const exportPdf = () => {
    setExportingPdf(true);
    try {
      const columns = ['Name', 'Email', 'Plan', 'Initial', 'Current', 'Growth', 'Referral', 'Total'];
      const rows = data.wallets.map(u => [
        u.name, u.email, u.subscription_plan || '-', fmt(u.initial_value), fmt(u.current_value),
        `${(u.growth || 0).toFixed(2)}%`, fmt(u.referral_earnings), fmt(u.total_value)
      ]);
      exportTableToPdf('Wallets Report', columns, rows, 'wallets_report.pdf', {
        stats: [{ label: 'Total Wallets', value: String(data.total) }]
      });
      toast.success('Wallets PDF exported');
    } catch (e) { toast.error('PDF export failed'); }
    setExportingPdf(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px]"><SearchBar value={search} onChange={setSearch} placeholder="Search wallets..." /></div>
        <FilterSelect value={plan} onChange={setPlan} placeholder="All Plans" options={(data.available_plans || []).map(p => ({ value: p, label: p }))} />
        <label className="flex items-center gap-2 text-sm text-[#A0B4CC] cursor-pointer select-none" data-testid="has-balance-toggle">
          <input type="checkbox" checked={hasBalance} onChange={(e) => setHasBalance(e.target.checked)} className="accent-[#00E5FF]" />
          Has balance only
        </label>
        <input type="number" min="0" step="any" value={minValue} onChange={(e) => setMinValue(e.target.value)} placeholder="Min ₱" className="w-28 h-9 px-3 rounded-md bg-[#000B18] border border-white/15 text-white text-sm" data-testid="wallet-min-value" />
        <input type="number" min="0" step="any" value={maxValue} onChange={(e) => setMaxValue(e.target.value)} placeholder="Max ₱" className="w-28 h-9 px-3 rounded-md bg-[#000B18] border border-white/15 text-white text-sm" data-testid="wallet-max-value" />
      </div>
      <ReportTableWrapper title="Wallets Report" onExport={exportCsv} exporting={exporting} onExportPdf={exportPdf} exportingPdf={exportingPdf}>
        {loading ? <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#00E5FF] mx-auto" /></div> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10">
                  <TableHead className="text-[#A0B4CC]">Name</TableHead>
                  <TableHead className="text-[#A0B4CC]">Email</TableHead>
                  <TableHead className="text-[#A0B4CC]">Plan</TableHead>
                  <TableHead className="text-[#A0B4CC]">Initial</TableHead>
                  <TableHead className="text-[#A0B4CC]">Current</TableHead>
                  <TableHead className="text-[#A0B4CC]">Growth</TableHead>
                  <TableHead className="text-[#A0B4CC]">Referral</TableHead>
                  <TableHead className="text-[#A0B4CC]">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.wallets.map(u => (
                  <TableRow key={u.id} className="border-white/5 hover:bg-white/5">
                    <TableCell className="text-white font-medium">{u.name}</TableCell>
                    <TableCell className="text-[#A0B4CC] text-sm">{u.email}</TableCell>
                    <TableCell className="text-white text-sm">{u.subscription_plan || '-'}</TableCell>
                    <TableCell className="font-mono text-[#5C7599] text-sm">{fmt(u.initial_value)}</TableCell>
                    <TableCell className="font-mono text-white text-sm">{fmt(u.current_value)}</TableCell>
                    <TableCell className={`font-mono text-sm ${u.growth >= 0 ? 'text-[#00E676]' : 'text-[#FF1744]'}`}>{u.growth?.toFixed(2)}%</TableCell>
                    <TableCell className="font-mono text-[#00E5FF] text-sm">{fmt(u.referral_earnings)}</TableCell>
                    <TableCell className="font-mono text-white font-semibold">{fmt(u.total_value)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </ReportTableWrapper>
      <Pagination skip={skip} limit={limit} total={data.total} onPageChange={setSkip} onLimitChange={(v) => { setLimit(v); setSkip(0); }} />
    </div>
  );
}
